import {objectFromEntries} from '@benchling/js-utils';
import {useCallback, useEffect, useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';
import {useNodesInitialized, useNodes, useEdges, Edge, Node, useReactFlow} from 'reactflow';

import {useIsNotUnmounted} from 'ui-library/reactflow/hooks/useIsNotUnmounted';

import {
  applyCustomLayoutSettings,
  CustomLayoutSettings,
  DimensionsForLayout,
  LayoutSettings,
} from './calculateDimensionsForLayout';
import {layoutFlowchartFromDimensions} from './layoutFlowchart';

/**
 * Calculate all dimensions expected by `layoutFlowchart`.
 */
function calculateDimensionsForLayout<N extends Node, E extends Edge>(
  nodes: Array<N>,
  edges: Array<E>,
  {
    calculateNodeDimensions,
    calculateEdgeLabelDimensions,
    horizontalSpaceBetweenNodes,
    verticalSpaceBetweenNodes,
    nodeOrigin,
  }: LayoutSettings<N, E>
): DimensionsForLayout {
  return {
    nodes: objectFromEntries(nodes.map((node) => [node.id, calculateNodeDimensions(node)])),
    edges: objectFromEntries(edges.map((edge) => [edge.id, calculateEdgeLabelDimensions(edge)])),
    horizontalSpaceBetweenNodes,
    verticalSpaceBetweenNodes,
    nodeOrigin,
  };
}

/**
 * Automatically layout a controlled `<ReactFlow>` flowchart.
 *
 * Use this hook within a `<ReactFlowProvider>`.
 *
 * The layout will automatically refresh when a node or edge is added or removed, or when a node's dimensions
 * change.
 *
 * Dimension calculations can be customized in `customLayoutSettings`; see `DEFAULT_LAYOUT_SETTINGS` for the
 * default calculations of dimensions.
 *
 * To prevent new nodes or edges from showing until their positions have been calculated, initialize the new
 * nodes and edges with `hideFlowchartElementBeforeLayout`. `useAutoLayout` will automatically
 * un-hide all nodes and edges, after calculating their positions.
 *
 * This layout algorithm does not guarantee nodes do not overlap edge labels.
 *
 * TODO(BNCH-125315): Automatically calculate edge labels' dimensions and all nodes and edges un-hidden
 *
 * @param customLayoutSettings Custom layout settings. See `DEFAULT_LAYOUT_SETTINGS` for defaults. By default,
 *    nodes' actual heights and widths, which are automatically calculated by `reactflow`, are used.
 * @returns Callback to force a refresh of the layout.
 */
export function useControlledAutoLayout<N extends Node, E extends Edge>(
  nodes: Array<N>,
  edges: Array<E>,
  setNodes: (nodes: Array<N>) => void,
  setEdges: (edges: Array<E>) => void,
  customLayoutSettings: CustomLayoutSettings<N, E> = {}
): () => void {
  const nodesInitialized = useNodesInitialized();

  const [dimensions, setDimensions] = useState<DimensionsForLayout | null>(null);

  const layoutSettings: LayoutSettings<N, E> = useMemo(
    () => applyCustomLayoutSettings(customLayoutSettings),
    [customLayoutSettings]
  );

  const isMountedRef = useIsNotUnmounted();

  useEffect(() => {
    if (!nodesInitialized || nodes.length === 0) {
      return;
    }

    const performLayout = async (): Promise<void> => {
      const updatedDimensions = calculateDimensionsForLayout(nodes, edges, layoutSettings);
      if (isEqual(dimensions, updatedDimensions)) {
        return;
      }

      const {nodes: updatedNodes, edges: updatedEdges} = await layoutFlowchartFromDimensions(
        {nodes, edges},
        updatedDimensions
      );

      if (isMountedRef.current) {
        setNodes(updatedNodes);
        setEdges(updatedEdges);
        setDimensions(updatedDimensions);
      }
    };

    performLayout();
  }, [
    nodes,
    edges,
    setNodes,
    setEdges,
    layoutSettings,
    nodesInitialized,
    dimensions,
    setDimensions,
    isMountedRef,
  ]);

  const refreshLayout = useCallback(() => {
    setDimensions(null);
  }, [setDimensions]);

  return refreshLayout;
}

/**
 * Automatically layout an uncontrolled `<ReactFlow>` flowchart, in which `reactflow` internally manages
 * nodes' and edges' state.
 *
 * Use this hook within a `<ReactFlowProvider>`, with `useNodesState` and `useEdgesState`.
 *
 * See `useControlledAutoLayout`.
 */
export function useUncontrolledAutoLayout<N extends Node, E extends Edge>(
  customLayoutSettings: CustomLayoutSettings<N, E> = {}
): () => void {
  const nodes = useNodes();
  const edges = useEdges();
  const {setNodes, setEdges} = useReactFlow();

  return useControlledAutoLayout(nodes, edges, setNodes, setEdges, customLayoutSettings);
}
